import { computed, shallowRef, ref, watch } from 'vue';
import { useRoute } from "vue-router";
/**
 * The active (selected) and the hovered item.
 *
 * Rows compare their item's id against `activeItemId` / `hoveredItemId`, so
 * only the affected rows re-render when either changes.
 */
export function useDataTableActiveItem(options) {
    const route = useRoute();
    // The item object (not only its id) is kept, because the `actions` slot
    //   and the `activate-item` event expose it, even after the item left the
    //   loaded slice.
    const activeItem = shallowRef(null);
    const activeItemId = computed(() => activeItem.value?.id ?? null);
    const hoveredItemId = shallowRef(null);
    // if set, this item will be activated once it is received by a load
    const activateOnLoad = ref(null);
    // list row elements by item id, maintained by the rows themselves
    const rowElements = new Map();
    watch(options.slice, () => {
        if (activateOnLoad.value) {
            const itemId = activateOnLoad.value;
            activateOnLoad.value = null;
            activateItemById(itemId);
        }
    });
    function activateItem(item) {
        activeItem.value = item;
        options.onActivate(item);
        if (!options.noScrollIntoView.value) {
            rowElements.get(item.id)?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }
    function activeItemIndex() {
        const id = activeItemId.value;
        return id === null ? -1 : options.slice.value.items.findIndex(item => item.id === id);
    }
    function activateNextItem() {
        const items = options.slice.value.items;
        const index = activeItemIndex();
        if (items.length > (index + 1)) {
            activateItem(items[index + 1]);
        }
    }
    function activatePrevItem() {
        const index = activeItemIndex();
        if (index > 0) {
            activateItem(options.slice.value.items[index - 1]);
        }
    }
    function activateItemById(itemId) {
        const item = options.slice.value.items.find(item => item.id === itemId);
        // we don't use activateItem() because we don't want to emit an event
        activeItem.value = item || null;
        if (!item) {
            activateOnLoad.value = itemId;
        }
    }
    function activateIfNoneActive(itemId) {
        if (!activeItem.value) {
            activateItemById(itemId);
        }
    }
    function getActiveItemRouteParam(routeParam) {
        const param = route.params[routeParam];
        if (Array.isArray(param))
            return param[0];
        else
            return param;
    }
    function activateItemByRoute() {
        const routeParam = options.activeItemIdRouteParam.value;
        if (!routeParam || !route || !route.params) {
            return;
        }
        const routeItemId = (typeof routeParam === 'function')
            ? routeParam()
            : getActiveItemRouteParam(routeParam);
        if (routeItemId) {
            activateItemById(routeItemId);
        }
    }
    function setHovered(itemId) {
        hoveredItemId.value = itemId;
    }
    return {
        activeItem,
        activeItemId,
        hoveredItemId,
        rowElements,
        activateItem,
        activateNextItem,
        activatePrevItem,
        activateItemById,
        activateIfNoneActive,
        activateItemByRoute,
        setHovered,
    };
}
